import { CustomField, fieldProps, lookupPipeline, renderLink } from './helpers'

const renderPositionLink = renderLink('/cat/positions')

const contractPipeline = lookupPipeline('cat_contracts', 'contract')
const statusPipeline = lookupPipeline('position_statuses', 'status')
const priorityPipeline = lookupPipeline('position_priorities', 'priority')
const contractTypePipeline = lookupPipeline('contract_types', 'contract_type')
const programManagerPipeline = lookupPipeline('program_managers', 'program_manager')
const hiringManagerPipeline = lookupPipeline('hiring_managers', 'hiring_manager')
const clearancePipeline = lookupPipeline('clearances', 'required_clearance')
const exemptStatusPipeline = lookupPipeline('position_exempt_statuses', 'exemptStatus')
const workSettingPipeline = lookupPipeline('positionworksettings', 'workSetting')

const requiredFields = ['id', 'title']

const fields: CustomField[] = [
  // Identity
  { name: 'id', label: 'Position ID', ...fieldProps.text, render: renderPositionLink },
  { name: 'title', label: 'Title', ...fieldProps.text, render: renderPositionLink },
  { name: 'location', label: 'Location', ...fieldProps.text },
  { name: 'numberOfOpenings', label: 'Number of Openings', ...fieldProps.number },

  // Contract
  { name: 'contract.name', label: 'Contract Name', pipeline: contractPipeline, ...fieldProps.text },
  { name: 'contract.abbreviation', label: 'Contract Abbreviation', pipeline: contractPipeline, ...fieldProps.text },

  // Status & Classification
  { name: 'status.name', label: 'Status', pipeline: statusPipeline, ...fieldProps.text },
  { name: 'priority.name', label: 'Priority', pipeline: priorityPipeline, ...fieldProps.text },
  { name: 'contract_type.name', label: 'Contract Type', pipeline: contractTypePipeline, ...fieldProps.text },
  { name: 'exemptStatus.name', label: 'Exempt Status', pipeline: exemptStatusPipeline, ...fieldProps.text },
  { name: 'workSetting.name', label: 'Work Setting', pipeline: workSettingPipeline, ...fieldProps.text },
  { name: 'required_clearance.name', label: 'Required Clearance', pipeline: clearancePipeline, ...fieldProps.text },

  // People
  { name: 'program_manager.name', label: 'Program Manager', pipeline: programManagerPipeline, ...fieldProps.text },
  { name: 'hiring_manager.name', label: 'Hiring Manager', pipeline: hiringManagerPipeline, ...fieldProps.text },

  // Compensation
  { name: 'bill_rate.min', label: 'Bill Rate Min', ...fieldProps.number },
  { name: 'bill_rate.max', label: 'Bill Rate Max', ...fieldProps.number },
  { name: 'salary.min', label: 'Salary Min', ...fieldProps.number },
  { name: 'salary.max', label: 'Salary Max', ...fieldProps.number },

  // Flags
  { name: 'backfill', label: 'Backfill', ...fieldProps.boolean },
  { name: 'temp_to_perm', label: 'Temp to Perm', ...fieldProps.boolean },
  { name: 'direct_hire', label: 'Direct Hire', ...fieldProps.boolean },

  // Dates
  { name: 'pricing.received', label: 'Pricing Received', ...fieldProps.date },
  { name: 'pricing.requested', label: 'Pricing Requested', ...fieldProps.date },
  { name: 'filled_date', label: 'Filled Date', ...fieldProps.date },
  { name: 'closed_date', label: 'Closed Date', ...fieldProps.date },
  { name: 'job_desc_received', label: 'Job Description Received', ...fieldProps.date },
  { name: 'job_desc_finalized', label: 'Job Description Finalized', ...fieldProps.date },
  { name: 'job_desc_posted', label: 'Job Description Posted', ...fieldProps.date },
  { name: 'createdAt', label: 'Created At', ...fieldProps.date },
  { name: 'updatedAt', label: 'Updated At', ...fieldProps.date },
  {
    name: 'candidate_count',
    label: 'Number of Candidates',
    pipeline: [
      {
        $lookup: {
          from: 'candidates',
          localField: '_id',
          foreignField: 'position',
          as: 'candidates'
        }
      },
      {
        $addFields: {
          candidate_count: { $size: '$candidates' }
        }
      }
    ],
    ...fieldProps.number
  },
  {
    name: 'daysOpen',
    label: 'Days Open',
    ...fieldProps.number,
    pipeline: [
      {
        $addFields: {
          startDate: {
            $ifNull: ['$job_desc_posted', null]
          },
          endDate: {
            $ifNull: [
              '$filled_date',
              '$closed_date',
              {
                $dateFromString: {
                  dateString: (new Date()).toISOString()
                }
              }
            ]
          }
        }
      },
      {
        $addFields: {
          hasDates: {
            $cond: [
              {
                $or: [
                  {
                    $eq: ['$startDate', null]
                  },
                  {
                    $eq: ['$endDate', null]
                  }
                ]
              },
              false,
              true
            ]
          }
        }
      },
      {
        $lookup: {
          from: 'holidays',
          pipeline: [
            {
              $project: {
                date: 1
              }
            }
          ],
          as: 'holidays'
        }
      },
      {
        $addFields: {
          holidays: {
            $map: {
              input: '$holidays',
              as: 'holiday',
              in: '$$holiday.date'
            }
          }
        }
      },
      {
        $addFields: {
          days: {
            $cond: [
              {
                $eq: ['$hasDates', false]
              },
              [],
              {
                $map: {
                  input: {
                    $range: [
                      0,
                      {
                        $dateDiff: {
                          startDate: '$startDate',
                          endDate: '$endDate',
                          unit: 'day'
                        }
                      }
                    ]
                  },
                  as: 'dayOffset',
                  in: {
                    $dateAdd: {
                      startDate: '$startDate',
                      unit: 'day',
                      amount: '$$dayOffset'
                    }
                  }
                }
              }
            ]
          }
        }
      },
      {
        $addFields: {
          workDays: {
            $filter: {
              input: '$days',
              as: 'date',
              cond: {
                $and: [
                  {
                    $ne: [
                      {
                        $isoDayOfWeek: '$$date'
                      },
                      6
                    ]
                  },
                  {
                    $ne: [
                      {
                        $isoDayOfWeek: '$$date'
                      },
                      7
                    ]
                  }
                ]
              }
            }
          }
        }
      },
      {
        $addFields: {
          daysOpen: {
            $size: '$workDays'
          }
        }
      }
    ]
  },

  // Other
  { name: 'keywords', label: 'Keywords', ...fieldProps.text }
]

export const config = {
  fields,
  requiredFields,
  requiredFieldObjects: fields.filter(f => requiredFields.includes(f.name))
}
